import DrawControl from '../../Mission/MissionView/MissionMap/DrawControl/DrawControl'
import drawStyles from '../../Mission/MissionView/MissionMap/DrawControl/drawStyles'
import CleaningPointsOnMap from '../../Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesNamesOnMap from '../../Mission/MissionView/MissionMap/Layers/CleaningRoutesNamesOnMap'
import CleaningRoutesOnMap from '../../Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import EditingControlsInstructions from '../../Mission/MissionView/Modes/DrawZone/ZoneDetails/EditingControlsInstructions'
import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import Title from 'components/Title/Title'
import { Feature, MultiLineString } from 'geojson'
import { ReactComponent as LocationSvg } from 'images/icons/location.svg'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { MapboxMap } from 'react-map-gl'
import { MISSION_MAP_DRAWING_MODES, PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement, IRecordedRoute, IRoute } from 'utils/interfaces'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import { zoomMapTo } from 'utils/mapUtils'

import RecordedRouteItem from './RecordedRouteItem/RecordedRouteItem'

import style from './RecordedRouteListView.module.css'
import tablePagination from 'styles/tablePagination.module.css'

const RecordedRouteListView = ({
  recordedRoutes,
  facilities = [],
  onRoutesChanged,
  currentPage,
  totalCount,
  pageSize,
  onPageChanged,
  searchQuery,
  onSearchChanged,
  selectedRouteId,
  onSelectedRouteIdChange,
}: {
  recordedRoutes: IRecordedRoute[]
  facilities?: any[]
  onRoutesChanged: () => void
  currentPage: number
  totalCount: number
  pageSize: number
  onPageChanged: (page: number) => void
  searchQuery: string
  onSearchChanged: (search: string) => void
  selectedRouteId?: number
  onSelectedRouteIdChange?: (id: number | undefined) => void
}) => {
  const { t } = useTranslation()
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const highlightedRoute = recordedRoutes.find(route => Number(route.id) === selectedRouteId)
  const highlightedZoneName = highlightedRoute?.properties.name
  const recordedRoutesRef = useRef(recordedRoutes)
  recordedRoutesRef.current = recordedRoutes
  const onSelectedRouteIdChangeRef = useRef(onSelectedRouteIdChange)
  onSelectedRouteIdChangeRef.current = onSelectedRouteIdChange
  const [zoomZoneName, setZoomZoneName] = useState<string | undefined>(undefined)
  const [pageInput, setPageInput] = useState(currentPage.toString())
  const [editMode, setEditMode] = useState(false)
  const [editingRoute, setEditingRoute] = useState<IRecordedRoute | undefined>(undefined)
  const [savingProgress, setSavingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [mapMode, setMapMode] = useState<MISSION_MAP_DRAWING_MODES>(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
  useSuppressNotificationSandwich(editMode, NOTIFICATION_SANDWICH_SUPPRESS.RECORDED_ROUTE_EDIT)

  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const initialZoomDoneRef = useRef(false)

  useEffect(() => {
    initialZoomDoneRef.current = false
  }, [currentPage, searchQuery])

  useEffect(() => {
    if (selectedRouteId != null) return
    if (map && recordedRoutes.length > 0 && !editingRoute && !initialZoomDoneRef.current) {
      zoomMapTo(map, [...recordedRoutes], 600)
      initialZoomDoneRef.current = true
    }
  }, [map, recordedRoutes, editingRoute, selectedRouteId])

  useEffect(() => {
    if (highlightedRoute?.properties.name) {
      setZoomZoneName(highlightedRoute.properties.name)
    }
  }, [selectedRouteId, highlightedRoute?.properties.name])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    let featuresToZoom: Feature[] = []

    if (zoomZoneName) {
      const index = recordedRoutes.findIndex(route => route.properties.name === highlightedZoneName)
      if (index !== -1 && recordedRoutes[index]) {
        featuresToZoom.push(recordedRoutes[index] as Feature)
      }
    }

    if (featuresToZoom.length > 0) {
      zoomMapTo(map, featuresToZoom, 600)
    }
  }, [zoomZoneName, highlightedZoneName, recordedRoutes])

  const prevEditingRouteIdRef = useRef<number | string | undefined>(undefined)

  useEffect(() => {
    if (editingRoute && map) {
      setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE)
      if (prevEditingRouteIdRef.current !== editingRoute.id) {
        zoomMapTo(map, [editingRoute], 600)
        prevEditingRouteIdRef.current = editingRoute.id
      }
    } else {
      setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
      prevEditingRouteIdRef.current = undefined
    }
  }, [editingRoute, map])

  const onMapInited = () => {
    const map = mapRef.current?.getMap()
    if (map) {
      setMap(map)
    }

    const selectRouteByName = (name: string | undefined) => {
      if (!name) {
        onSelectedRouteIdChangeRef.current?.(undefined)
        return
      }
      const found = recordedRoutesRef.current.find(route => route.properties.name === name)
      onSelectedRouteIdChangeRef.current?.(found?.id != null ? Number(found.id) : undefined)
    }
    map?.on('click', () => selectRouteByName(undefined))
    map?.on('click', 'gl-draw-cleaning-routes', (e: any) =>
      selectRouteByName(e?.features && e?.features[0].properties?.name)
    )
    map?.on('click', 'recorded-route-text-layer', (e: any) =>
      selectRouteByName(e?.features && e?.features[0].properties?.name)
    )
  }

  const onZoneClicked = useCallback(
    (route: IRecordedRoute) => {
      const id = route.id != null ? Number(route.id) : undefined
      onSelectedRouteIdChange?.(id)
      setZoomZoneName(route.properties.name)
    },
    [onSelectedRouteIdChange]
  )

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / pageSize) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        onPageChanged(val)
      } else {
        setPageInput(currentPage.toString())
      }
    }
  }

  const startEditRoute = useCallback((route: IRecordedRoute) => {
    setEditingRoute(route)
    setEditMode(true)
  }, [])

  const onUpdateRoute = (evt: { features: Feature[]; action: string }) => {
    const feature = evt.features[0]
    if (feature && editingRoute) {
      setEditingRoute({
        ...editingRoute,
        geometry: feature.geometry as MultiLineString,
      })
    }
  }

  const onSaveRoute = async () => {
    if (editingRoute && editingRoute.id) {
      setSavingProgress(PROGRESS_TYPES.WORK)
      try {
        await MainApi.updateRecordedRouteGeometry(editingRoute.id as number, editingRoute.geometry, false)
        setEditMode(false)
        setEditingRoute(undefined)
        onRoutesChanged()
      } catch (e) {
        console.error(e)
      } finally {
        setSavingProgress(PROGRESS_TYPES.IDLE)
      }
    }
  }

  const onCancelEdit = () => {
    setEditMode(false)
    setEditingRoute(undefined)
  }

  const maxPage = Math.ceil(totalCount / pageSize) || 1

  const visibleRoutes = useMemo(
    () => recordedRoutes.filter(r => r.id !== editingRoute?.id),
    [recordedRoutes, editingRoute?.id]
  )

  const routesForPoints = useMemo(
    () => recordedRoutes.map(r => (r.id === editingRoute?.id ? (editingRoute as IRecordedRoute) : r)),
    [recordedRoutes, editingRoute]
  )

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.headerArea}>
          <div className={style.titleBlock}>
            <Title text={'Recorded routes'} />{' '}
          </div>
          {!editMode && (
            <div className={style.controlsBlock}>
              <input
                className={style.searchInput}
                type="text"
                placeholder="Search routes..."
                value={searchQuery}
                onChange={e => onSearchChanged(e.target.value)}
              />
            </div>
          )}
        </div>
        <Box className={style.box}>
          {!editMode ? (
            <div className={style.tableWrapper}>
              <div className={style.tableHeader}>
                <div></div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>#</div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>Name</div>
                <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Length</div>
                <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Facility</div>
                <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>
                  <LocationSvg width={18} height={18} className={style.themedIcon} title="Localization" />
                </div>
                <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Map name</div>
                <div></div>
              </div>
              {recordedRoutes.length ? (
                <>
                  <div className={style.table}>
                    {recordedRoutes.map((route, index) => (
                      <RecordedRouteItem
                        key={route.id}
                        route={route}
                        facilities={facilities}
                        index={(currentPage - 1) * pageSize + index + 1}
                        highlighted={route.properties.name === highlightedZoneName}
                        onRoutesChanged={onRoutesChanged}
                        onClicked={() => onZoneClicked(route)}
                        onEditClicked={() => startEditRoute(route)}
                      />
                    ))}
                  </div>
                  {totalCount > pageSize && (
                    <div className={tablePagination.pagination}>
                      <button
                        className={tablePagination.paginationButton}
                        onClick={() => onPageChanged(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        &larr;
                      </button>
                      <span className={tablePagination.pageInfo}>
                        <input
                          className={tablePagination.pageInput}
                          value={pageInput}
                          onChange={e => setPageInput(e.target.value)}
                          onKeyDown={handlePageJump}
                          onBlur={() => setPageInput(currentPage.toString())}
                        />
                        / {maxPage}
                      </span>
                      <button
                        className={tablePagination.paginationButton}
                        onClick={() => onPageChanged(currentPage + 1)}
                        disabled={currentPage >= maxPage}
                      >
                        &rarr;
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>{t('view_zones.empty_zone_list')}</div>
                  <div className={style.emptyDescription}>{t('view_zones.empty_zone_list_description')}</div>
                </div>
              )}
            </div>
          ) : (
            <div className={style.editPanel}>
              <div className={style.editTitle}>Editing of the {editingRoute?.properties?.name}</div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexShrink: 0,
                  paddingLeft: '12px',
                  paddingRight: '8px',
                }}
              >
                <EditingControlsInstructions forceRouteMode={true} forceDrawingMode={false} />
              </div>
              <div className={style.buttons}>
                <Button.Outlined
                  className={style.button}
                  onClick={onCancelEdit}
                  disabled={savingProgress === PROGRESS_TYPES.WORK}
                >
                  {t('common.cancel')}
                </Button.Outlined>
                <Button.Standart
                  className={style.button}
                  onClick={onSaveRoute}
                  loading={savingProgress === PROGRESS_TYPES.WORK}
                  disabled={savingProgress === PROGRESS_TYPES.WORK}
                >
                  {t('common.save')}
                </Button.Standart>
              </div>
            </div>
          )}
        </Box>
        <div className={style.mapWrapper}>
          <MapboxCommon ref={mapRef} overlayItems={[]} onMapInited={onMapInited} maxZoom={23}>
            <CleaningRoutesOnMap
              cleaningRoutes={visibleRoutes}
              hasSelection={!!highlightedZoneName || editMode}
              editingRoute={editMode && editingRoute ? editingRoute : undefined}
              highlightedName={highlightedZoneName}
            />
            <CleaningRoutesNamesOnMap cleaningRoutes={visibleRoutes} />
            <CleaningPointsOnMap
              cleaningRoutes={routesForPoints}
              launchPoints={[]}
              editingRouteId={editingRoute?.id as number}
              highlightedName={highlightedZoneName}
            />
            {editMode && editingRoute && (
              <DrawControl
                userProperties={true}
                displayControlsDefault={false}
                onCreate={() => {}}
                onUpdate={onUpdateRoute}
                onDelete={() => {}}
                mode={mapMode}
                tempZoneRoute={{ ...editingRoute, id: String(editingRoute.id) } as unknown as IRoute}
                tempZone={undefined}
                tempLaunchPoint={undefined}
                tempTransfer={undefined}
                tempMissionPoint={undefined}
                styles={drawStyles}
                setEditingTempLaunchPoint={() => {}}
                setEditingTempZone={() => {}}
                setEditingTempZoneRoute={() => {}}
              />
            )}
          </MapboxCommon>
        </div>
      </div>
    </div>
  )
}

export default RecordedRouteListView
